import { Injectable, signal } from '@angular/core';
import { ListExportService, StorageService, TripsService } from '../services';
import { ITrip, IBase, ITripWithPositions } from '../models';
import { objectToCamel } from 'ts-case-convert';
import { initialPaginationState } from '../store/initialisation/pagination';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { initialTripsState } from '../store/initialisation/trips';
import { ExportableListType, ExpeditionStatus, ExpeditionType, ServiceType, TripsViewMode } from '../models/enums';
import { APP } from '../config/app';
import { SuggestionService } from '../services/suggestion/suggestion.service';
import { ITripsState } from '../store/states';
import {
  DEFAULT_TRIP_STATUS,
  ITripsFilters,
  deserializeTripsFilters,
  emptyTripsFilters,
  parseTripsFiltersFromStorage,
  resolvePositionsDayRange,
} from '../filters';

@Injectable({
  providedIn: 'root',
})
export class TripsResolver {
  // Tracks the latest list-fetch request; stale completions are dropped so a
  // page change isn't overwritten by a fingerprint-dedup'd earlier call.
  private listGeneration = 0;
  // Fingerprint of the last successful fetch. Skips redundant resolver runs on
  // sibling-route navigation (/trips -> /trips/:id) so the list doesn't refetch
  // and lose the current page.
  private lastFetchFingerprint: string | null = null;
  // Fingerprint without page. Lets us tell a pure page change from a filter
  // change so the pagination only collapses to "currentPage ···" when totals
  // are genuinely unknown.
  private lastFilterFingerprint: string | null = null;

  readonly tripsWithPositions = signal<ITripWithPositions[]>([]);

  constructor(
    private tripsService: TripsService,
    public storageService: StorageService,
    private suggestionService: SuggestionService,
    private listExportService: ListExportService,
  ) {}

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async resolve(route: ActivatedRouteSnapshot, state?: RouterStateSnapshot) {
    this.storageService.setRouteName('trips')
    const size = this.pageSize(route);
    const search = route.queryParamMap.get('search') ?? undefined;
    const filters = this.applyViewModeFilters(route, await this.resolveFilters(route));
    const isPositionsView = this.isPositionsView(route);

    const filterKey = { ...this.filterKey(filters), positions: isPositionsView };
    const filterFingerprint = JSON.stringify({ size, search: search ?? null, ...filterKey });
    // A genuine search/filter change explicitly clears `page` from the URL to reset
    // pagination — don't let that be confused with the sibling-route case below, where
    // `page` is also absent from the child route's params but the current page should stick.
    const filtersChanged = filterFingerprint !== this.lastFilterFingerprint;
    const page = this.resolvePage(route, filtersChanged);

    const fingerprint = JSON.stringify({ size, page, search: search ?? null, ...filterKey });
    const listAlreadyFetched = fingerprint === this.lastFetchFingerprint && !!initialTripsState.value?.length;
    this.registerListExport(filters, search, page, size, listAlreadyFetched);
    if (listAlreadyFetched) {
      return;
    }

    initialTripsState.isFetchingTrips = true;
    initialTripsState.value = [];
    this.tripsWithPositions.set([]);
    if (filtersChanged) {
      initialTripsState.pagination.totalPages = 0;
      initialTripsState.pagination.totalElements = 0;
    }
    this.lastFilterFingerprint = filterFingerprint;

    this.resolveAsync(size, page, filters, search, fingerprint, isPositionsView);
  }

  async refreshCurrentPage(route: ActivatedRouteSnapshot) {
    const filters = this.applyViewModeFilters(route, await this.resolveFilters(route));
    const search = route.queryParamMap.get('search') ?? undefined;
    const isPositionsView = this.isPositionsView(route);
    await this.resolveAsync(this.pageSize(route), this.resolvePage(route), filters, search, undefined, isPositionsView);
  }

  private isPositionsView(route: ActivatedRouteSnapshot): boolean {
    return route.firstChild?.data['viewMode'] === TripsViewMode.POSITIONS;
  }

  private pageSize(route: ActivatedRouteSnapshot): number {
    if (this.isPositionsView(route)) return APP.POSITIONS_PAGE_SIZE;
    return route.data['trips']?.pagination.number_of_elements || APP.TRIPS_PAGE_SIZE;
  }

  private applyViewModeFilters(route: ActivatedRouteSnapshot, filters: ITripsFilters): ITripsFilters {
    if (!this.isPositionsView(route)) return filters;
    const { day } = resolvePositionsDayRange(route.queryParamMap);
    return { ...filters, from: day, to: day };
  }

  private fetchPage(
    size: number,
    page: number,
    filters: ITripsFilters,
    search: string | undefined,
    isPositionsView: boolean,
  ) {
    if (isPositionsView) return this.tripsService.getTripsPositions(size, page, filters, search);
    return this.tripsService.getTripsFiltered(
      size, page,
      filters.selectedClients, filters.selectedOperators, filters.selectedTowns,
      filters.selectedTypes, filters.selectedStatus, search,
      filters.selectedServices, filters.selectedRoutes,
      undefined, filters.from, filters.to, filters.selectedAvailabilityId,
      filters.selectedDrivers, filters.selectedVehicles, filters.selectedServiceTypes, filters.selectedItineraryId
    );
  }

  private extractTrips(content: any[], isPositionsView: boolean): ITrip[] {
    if (!isPositionsView) {
      this.tripsWithPositions.set([]);
      return content;
    }
    const tripsWithPositions = objectToCamel(content) as ITripWithPositions[];
    this.tripsWithPositions.set(tripsWithPositions);
    return tripsWithPositions;
  }

  private filterKey(filters: ITripsFilters) {
    return {
      clients: filters.selectedClients.map((c) => c.id).sort(),
      operators: filters.selectedOperators.map((o) => o.id).sort(),
      towns: filters.selectedTowns.map((t) => t.id).sort(),
      services: filters.selectedServices.map((s) => s.id).sort(),
      routes: filters.selectedRoutes.map((r) => r.id).sort(),
      drivers: filters.selectedDrivers.map((d) => d.id).sort(),
      vehicles: filters.selectedVehicles.map((v) => v.id).sort(),
      types: [...filters.selectedTypes].sort(),
      serviceTypes: [...filters.selectedServiceTypes].sort(),
      status: [...filters.selectedStatus].sort(),
      from: filters.from?.toISOString() ?? null,
      to: filters.to?.toISOString() ?? null,
      availability: filters.selectedAvailabilityId,
      itinerary: filters.selectedItineraryId,
    };
  }

  private registerListExport(
    filters: ITripsFilters,
    search: string | undefined,
    page: number,
    size: number,
    listAlreadyFetched: boolean,
  ) {
    const { totalElements, numberOfElements } = initialTripsState.pagination;
    this.listExportService.register(
      ExportableListType.TRIPS,
      (visiblePage) => this.tripsService.exportTrips(filters, search, visiblePage),
      { page, size },
      listAlreadyFetched ? { allRows: totalElements, visibleRows: numberOfElements } : null,
    );
  }

  private resolvePage(route: ActivatedRouteSnapshot, forceFirstPage = false): number {
    const pageParam = route.queryParamMap.get('page');
    const pageFromUrl = pageParam ? Number(pageParam) : null;
    if (pageFromUrl && !isNaN(pageFromUrl) && pageFromUrl > 0) return pageFromUrl;
    if (forceFirstPage) return 1;
    return route.data['trips']?.pagination.actualPage || initialTripsState.pagination.actualPage || 1;
  }

  private async resolveFilters(route: ActivatedRouteSnapshot): Promise<ITripsFilters> {
    // URL is the source of truth whenever there is *any* query param. Storage is only
    // consulted on a truly bare URL (the "sidebar tab-switch" entry path).
    if (route.queryParamMap.keys.length > 0) {
      return deserializeTripsFilters(route.queryParamMap);
    }
    const stored = parseTripsFiltersFromStorage(await this.storageService.getTripsFilters());
    if (stored) {
      return {
        ...stored,
        selectedStatus: stored.selectedStatus?.length ? stored.selectedStatus : DEFAULT_TRIP_STATUS,
      };
    }
    return emptyTripsFilters();
  }

  async resolveAsync(
    size: number,
    page: number,
    filters: ITripsFilters,
    search?: string,
    fingerprint?: string,
    isPositionsView = false,
  ) {
    const myGen = ++this.listGeneration;
    const {
      content,
      number_of_elements,
      total_elements,
      total_pages,
      first,
      last,
    } = await this.fetchPage(size, page, filters, search, isPositionsView);
    // Drop the response if a newer resolver call has started in the meantime.
    if (myGen !== this.listGeneration) return;
    const trips = this.extractTrips(content, isPositionsView);
    const result = this.getData(trips, number_of_elements, total_elements, total_pages, first, last, page);
    this.listExportService.setRowCounts(ExportableListType.TRIPS, {
      allRows: total_elements,
      visibleRows: number_of_elements,
    });
    if (fingerprint !== undefined) this.lastFetchFingerprint = fingerprint;
    return result;
  }

  async getTrips(
    route: ActivatedRouteSnapshot,
    expeditionClients: IBase[],
    expeditionOperators: IBase[],
    expeditionTowns: IBase[],
    expeditionTypes: ExpeditionType[],
    expeditionStatus: ExpeditionStatus[],
    searchValue?: string,
    expeditionServices?: IBase[],
    expeditionRoutes?: IBase[],
    from?: Date,
    to?: Date,
    availabilityId?: number | null,
    expeditionDrivers?: IBase[],
    expeditionVehicles?: IBase[],
    expeditionServiceTypes?: ServiceType[],
    itineraryId?: number | null,
  ) {
    const page = this.resolvePage(route);
    const size = this.pageSize(route);
    const filters = this.applyViewModeFilters(route, {
      selectedClients: expeditionClients,
      selectedOperators: expeditionOperators,
      selectedTowns: expeditionTowns,
      selectedServices: expeditionServices ?? [],
      selectedRoutes: expeditionRoutes ?? [],
      selectedDrivers: expeditionDrivers ?? [],
      selectedVehicles: expeditionVehicles ?? [],
      selectedTypes: expeditionTypes,
      selectedServiceTypes: expeditionServiceTypes ?? [],
      selectedStatus: expeditionStatus,
      from,
      to,
      selectedAvailabilityId: availabilityId ?? null,
      selectedItineraryId: itineraryId ?? null,
    });
    const isPositionsView = this.isPositionsView(route);
    const {
      content,
      number_of_elements,
      total_elements,
      total_pages,
      first,
      last,
    } = await this.fetchPage(size, page, filters, searchValue, isPositionsView);
    return this.getData(
      this.extractTrips(content, isPositionsView),
      number_of_elements,
      total_elements,
      total_pages,
      first,
      last,
      page,
    );
  }

  getData = (
    content: any,
    number_of_elements: any,
    total_elements: any,
    total_pages: any,
    first: any,
    last: any,
    page: any,
  ) => {
    const data = initialTripsState;
    data.value = objectToCamel(content) as Array<ITrip>;
    data.isFetchingTrips = false;
    data.pagination = {...initialPaginationState};
    data.pagination.numberOfElements = number_of_elements;
    data.pagination.totalPages = total_pages;
    data.pagination.totalElements = total_elements;
    data.pagination.actualPage = page;
    data.pagination.first = first;
    data.pagination.last = last;
    this.loadActiveTickets(data);
    return data;
  };

  loadActiveTickets(data: ITripsState) {
    const trips = data.value;
    this.suggestionService
      .getActiveTickets(trips.map((trip) => trip.id))
      .then((activeTicketsByExpedition) => {
        if (data.value === trips) data.activeTicketsByExpedition = activeTicketsByExpedition;
      })
      .catch(() => (data.activeTicketsByExpedition = {}));
  }
}
